/**
 * AutomationFlowContext - Provides shared callbacks and state for automation flow components
 *
 * This context allows deeply nested components (like SegmentNode) to access
 * callbacks without prop drilling through React Flow's node data system.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type * as Api from '@ViewModels';

interface AutomationFlowContextValue {
	/** Whether this hook is currently reading from a real provider instance. */
	hasProvider: boolean;
	/** Callback to add a new path/case to a switch node */
	onAddPath?: (switchNodeId: string) => Promise<Api.ISwitchAutomationStep | void | null>;
	/** Callback to update node data */
	updateNodeData?: (nodeId: string, data: Record<string, unknown>) => void;
	onUpdateCaseCriteria?: (
		switchNodeId: string,
		caseId: string,
		filter: Api.IContactFilterCriteria | undefined,
		stageIds?: string[],
		prompt?: string
	) => void;
	/**
	 * Callback to persist segment case stage gating changes to the API. Only honored by the server
	 * when the parent template's trigger is `OpportunityCreatedAutomationTrigger`. Pass an empty
	 * array to clear stage gating ("any stage"). The `filter` arg lets the caller write the
	 * canonical filter alongside stageIds in one PUT — avoids a race where the optimistic case
	 * still holds a pre-clear filter.
	 */
	onUpdateCaseStageIds?: (
		switchNodeId: string,
		caseId: string,
		stageIds: string[],
		filter?: Api.IContactFilterCriteria,
		prompt?: string
	) => void;
	/** Callback to persist segment case name changes to the API */
	onUpdateCaseName?: (switchNodeId: string, caseId: string, name: string) => void;
	onUpdateCasePrompt?: (
		switchNodeId: string,
		caseId: string,
		prompt: string,
		filter: Api.IContactFilterCriteria | undefined,
		stageIds: string[]
	) => void;
	/** Opens the canvas node context menu at the given screen coordinates */
	onOpenNodeMenu?: (nodeId: string, position: { x: number; y: number }) => void;
	/** Whether the Add Path button should be shown on segment nodes */
	showAddPath: boolean;
	/** Whether the canvas is in readonly mode */
	isReadOnly: boolean;
	/** Live automation template from the query. */
	automationTemplate?: Api.IAutomationTemplate;
	/** Available connection types for step send-from options. */
	connectionTypes?: Api.IConnectionType[];
	/** Impersonation context for admin operations */
	impersonationContext?: Api.IImpersonationContext;
	allowAIConditionals?: boolean;
}

const AutomationFlowContext = createContext<AutomationFlowContextValue | null>(null);

interface AutomationFlowProviderProps {
	children: ReactNode;
	onAddPath?: (switchNodeId: string) => Promise<Api.ISwitchAutomationStep | void | null>;
	updateNodeData?: (nodeId: string, data: Record<string, unknown>) => void;
	onUpdateCaseCriteria?: (
		switchNodeId: string,
		caseId: string,
		filter: Api.IContactFilterCriteria | undefined,
		stageIds?: string[],
		prompt?: string
	) => void;
	onUpdateCaseStageIds?: (
		switchNodeId: string,
		caseId: string,
		stageIds: string[],
		filter?: Api.IContactFilterCriteria,
		prompt?: string
	) => void;
	onUpdateCaseName?: (switchNodeId: string, caseId: string, name: string) => void;
	onUpdateCasePrompt?: (
		switchNodeId: string,
		caseId: string,
		prompt: string,
		filter: Api.IContactFilterCriteria | undefined,
		stageIds: string[]
	) => void;
	onOpenNodeMenu?: (nodeId: string, position: { x: number; y: number }) => void;
	showAddPath?: boolean;
	isReadOnly?: boolean;
	automationTemplate?: Api.IAutomationTemplate;
	connectionTypes?: Api.IConnectionType[];
	impersonationContext?: Api.IImpersonationContext;
	allowAIConditionals?: boolean;
}

export function AutomationFlowProvider({
	children,
	onAddPath,
	updateNodeData,
	onUpdateCaseCriteria,
	onUpdateCaseStageIds,
	onUpdateCaseName,
	onUpdateCasePrompt,
	onOpenNodeMenu,
	showAddPath = false,
	isReadOnly = false,
	automationTemplate,
	connectionTypes,
	impersonationContext,
	allowAIConditionals = false,
}: AutomationFlowProviderProps) {
	// Memoize the context value to prevent all consumer nodes from
	// re-rendering on every parent render (the canvas re-renders frequently
	// during drag/resize).
	const value = useMemo<AutomationFlowContextValue>(
		() => ({
			hasProvider: true,
			onAddPath,
			updateNodeData,
			onUpdateCaseCriteria,
			onUpdateCaseStageIds,
			onUpdateCaseName,
			onUpdateCasePrompt,
			onOpenNodeMenu,
			showAddPath,
			isReadOnly,
			automationTemplate,
			connectionTypes,
			impersonationContext,
			allowAIConditionals,
		}),
		[
			onAddPath,
			updateNodeData,
			onUpdateCaseCriteria,
			onUpdateCaseStageIds,
			onUpdateCaseName,
			onUpdateCasePrompt,
			onOpenNodeMenu,
			showAddPath,
			isReadOnly,
			automationTemplate,
			connectionTypes,
			impersonationContext,
			allowAIConditionals,
		]
	);

	return <AutomationFlowContext.Provider value={value}>{children}</AutomationFlowContext.Provider>;
}

export function useAutomationFlowContext(): AutomationFlowContextValue {
	const context = useContext(AutomationFlowContext);
	if (!context) {
		// Return safe defaults if used outside provider (e.g., in Storybook)
		return {
			hasProvider: false,
			showAddPath: false,
			isReadOnly: true,
		};
	}
	return context;
}
